import {
  actionTypes as dataFetchActionTypes,
  actions as dataFetchActions,
} from '@buffer-mono/async-data-fetch'
import { toast } from '@buffer-mono/popcorn'
import type { Middleware } from '@reduxjs/toolkit'

import type { AppDispatch, RootState } from '~publish/legacy/store'
import { isLegacyAction } from '~publish/legacy/store/isAction'

import { actions as analyticsActions } from '~publish/legacy/analytics-middleware/actions'
import { actionTypes as organizationActionTypes } from '~publish/legacy/organizations'

import { createHashtagGroupViaGraphQL } from './createHashtagGroup'
import { deleteHashtagGroupViaGraphQL } from './deleteHashtagGroup'
import { getHashtagGroupsViaGraphQL } from './getHashtagGroups'
import { actionTypes } from './reducer'
import { updateHashtagGroupViaGraphQL } from './updateHashtagGroup'
import countHashtagsInText from './utils/HashtagCounter'

type TrackingMetadata = {
  hashtagGroupName: string
  hashtagCount: number
  product: 'publish'
}

const toErrorMessage = (error: unknown): string =>
  error instanceof Error
    ? error.message
    : typeof error === 'string'
      ? error
      : String(error)

// Monotonic id so a stale refreshHashtagGroups response cannot overwrite a newer one
// (e.g. overlapping ORGANIZATION_SELECTED / update-delete refreshes).
let hashtagGroupsLoadGeneration = 0

const refreshHashtagGroups = (
  dispatch: AppDispatch,
  organizationId: string | undefined,
): void => {
  // Bump even when organizationId is missing so clearing selection invalidates
  // an in-flight refresh for the previous org.
  const loadId = ++hashtagGroupsLoadGeneration
  if (!organizationId) {
    return
  }
  const args = { organizationId }
  void (async (): Promise<void> => {
    try {
      const groups = await getHashtagGroupsViaGraphQL(args)
      if (loadId !== hashtagGroupsLoadGeneration) return
      dispatch(
        dataFetchActions.fetchSuccess({
          name: 'getHashtagGroups',
          args,
          result: { data: { snippets: groups } },
        }),
      )
    } catch (error: unknown) {
      if (loadId !== hashtagGroupsLoadGeneration) return
      dispatch(
        dataFetchActions.fetchFail({
          name: 'getHashtagGroups',
          args,
          error: toErrorMessage(error),
        }),
      )
    }
  })()
}

const getTrackingMetadataFromStoryGroup = (
  name: string,
  text: string,
): TrackingMetadata => {
  return {
    hashtagGroupName: name,
    hashtagCount: countHashtagsInText(text),
    product: 'publish',
  }
}

const middleware: Middleware<object, RootState, AppDispatch> =
  ({ getState, dispatch }) =>
  (next) =>
  (action): void => {
    next(action)
    if (!isLegacyAction(action)) return
    const organizationId = getState().organizations?.selected?.id
    switch (action.type) {
      case actionTypes.SAVE_HASHTAG_GROUP: {
        const { name, text } = getState().hashtagGroups
        if (!organizationId) {
          // Reducer already added optimistic `temp`; clear it via FETCH_FAIL.
          dispatch(
            dataFetchActions.fetchFail({
              name: 'createHashtagGroup',
              args: { organizationId, name, text },
              error: 'Unable to save hashtag group',
            }),
          )
          break
        }
        const args = {
          organizationId,
          name,
          text,
        }
        void (async (): Promise<void> => {
          try {
            const created = await createHashtagGroupViaGraphQL(args)
            // GraphQL returns the created snippet only; rebuild the list the
            // reducer expects by replacing the optimistic temp entry.
            const groups = [
              ...getState().hashtagGroups.groups.filter(
                (group) => group._id !== 'temp' && group._id !== created._id,
              ),
              created,
            ].toSorted((a, b) => (a.name < b.name ? -1 : 1))
            dispatch(
              dataFetchActions.fetchSuccess({
                name: 'createHashtagGroup',
                args,
                result: { data: { snippets: groups } },
              }),
            )
          } catch (error: unknown) {
            dispatch(
              dataFetchActions.fetchFail({
                name: 'createHashtagGroup',
                args,
                error: toErrorMessage(error),
              }),
            )
          }
        })()
        break
      }
      case actionTypes.UPDATE_HASHTAG_GROUP: {
        const { name, text, snippetId } = getState().hashtagGroups
        if (!organizationId || !snippetId) {
          // Reducer already applied optimistic UPDATE; recover via FETCH_FAIL
          // (toast + refreshHashtagGroups), same as the GraphQL catch path.
          dispatch(
            dataFetchActions.fetchFail({
              name: 'updateHashtagGroup',
              args: { organizationId, snippetId, name, text },
              error: 'Unable to update hashtag group',
            }),
          )
          break
        }
        const args = {
          organizationId,
          snippetId,
          name,
          text,
        }
        void (async (): Promise<void> => {
          try {
            const updated = await updateHashtagGroupViaGraphQL(args)
            const groups = getState().hashtagGroups.groups.map((group) =>
              group._id === updated._id ? updated : group,
            )
            dispatch(
              dataFetchActions.fetchSuccess({
                name: 'updateHashtagGroup',
                args,
                result: { data: { snippets: groups } },
              }),
            )
          } catch (error: unknown) {
            dispatch(
              dataFetchActions.fetchFail({
                name: 'updateHashtagGroup',
                args,
                error: toErrorMessage(error),
              }),
            )
          }
        })()
        break
      }
      case actionTypes.DELETE_HASHTAG_GROUP: {
        const snippetId = action.snippetId
        if (!organizationId || !snippetId) {
          break
        }
        dispatch(
          analyticsActions.trackEvent(
            'Hashtag Group Deleted',
            getTrackingMetadataFromStoryGroup(action.name, action.text),
          ),
        )
        const args = {
          organizationId,
          snippetId,
        }
        void (async (): Promise<void> => {
          try {
            await deleteHashtagGroupViaGraphQL(args)
            dispatch(
              dataFetchActions.fetchSuccess({
                name: 'deleteHashtagGroup',
                args,
                result: { success: true },
              }),
            )
          } catch (error: unknown) {
            dispatch(
              dataFetchActions.fetchFail({
                name: 'deleteHashtagGroup',
                args,
                error: toErrorMessage(error),
              }),
            )
          }
        })()
        break
      }
      case `createHashtagGroup_${dataFetchActionTypes.FETCH_FAIL}`:
        toast.error(action.error)
        break
      case `updateHashtagGroup_${dataFetchActionTypes.FETCH_FAIL}`:
        toast.error(action.error)
        refreshHashtagGroups(dispatch, organizationId)
        break
      case `deleteHashtagGroup_${dataFetchActionTypes.FETCH_FAIL}`:
        toast.error(action.error)
        refreshHashtagGroups(dispatch, organizationId)
        break
      case organizationActionTypes.ORGANIZATION_SELECTED:
        refreshHashtagGroups(dispatch, organizationId)
        break
      case actionTypes.INSERT_HASHTAG_GROUP:
        dispatch(
          analyticsActions.trackEvent(
            'Hashtag Group Inserted',
            getTrackingMetadataFromStoryGroup(action.name, action.text),
          ),
        )
        break
      case `createHashtagGroup_${dataFetchActionTypes.FETCH_SUCCESS}`:
        dispatch(
          analyticsActions.trackEvent(
            'Hashtag Group Created',
            getTrackingMetadataFromStoryGroup(
              action.args.name,
              action.args.text,
            ),
          ),
        )
        break
      case `updateHashtagGroup_${dataFetchActionTypes.FETCH_SUCCESS}`: {
        dispatch(
          analyticsActions.trackEvent(
            'Hashtag Group Updated',
            getTrackingMetadataFromStoryGroup(
              action.args.name,
              action.args.text,
            ),
          ),
        )
        refreshHashtagGroups(dispatch, organizationId)
        break
      }
      case `deleteHashtagGroup_${dataFetchActionTypes.FETCH_SUCCESS}`: {
        toast.success('Hashtag group deleted')
        break
      }
      default:
        break
    }
  }

export default middleware
